'use client';

import { useCallback, useEffect } from 'react';
import { type NotificationProps, Notification } from './Notification';

export type ToastNotificationProps = {
  toastId: number;
  message?: string;
  duration?: number;
  title?: string;
  ContentOverride?: React.ComponentType<{ closeNotification: () => void }>;
} & Pick<NotificationProps, 'severity' | 'onClose' | 'onClickClose'>;

export const ToastNotification = ({
  toastId,
  title,
  message,
  duration = 30000,
  severity,
  onClose,
  onClickClose,
  removeNotification,
  ContentOverride,
}: ToastNotificationProps & { removeNotification: (toastId: number) => void }) => {
  const closeNotification = useCallback(() => {
    removeNotification(toastId);
    onClose && onClose();
  }, [toastId, onClose, removeNotification]);

  useEffect(() => {
    let timeout: NodeJS.Timeout | undefined;
    if (duration) {
      timeout = setTimeout(() => {
        closeNotification();
      }, duration);
    }
    return () => {
      if (timeout) {
        clearTimeout(timeout);
      }
    };
  }, [duration, closeNotification]);

  return (
    <Notification
      severity={severity}
      variant="default"
      onClickClose={onClickClose}
      onClose={onClose ? closeNotification : undefined}
    >
      {(ContentOverride && <ContentOverride closeNotification={closeNotification} />) || (
        <>
          {title && <p className="text-size-body-base">{title}</p>}
          <p className="text-size-body-sm">{message}</p>
        </>
      )}
    </Notification>
  );
};
